import React from 'react';
import { Paper, Stack, Typography, Button, Chip, Box } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import PersonOffOutlinedIcon from '@mui/icons-material/PersonOffOutlined';
import { MemberStatus, ClinicalFlag, FLAG_COLORS } from '../Data/enums';

const getClinicalFlags = (patient) => {
  const flags = [];
  if (patient.isAdmitted) flags.push(ClinicalFlag.ADMITTED);
  if (patient.isDischarged) flags.push(ClinicalFlag.DISCHARGED);
  if (patient.isEmergency) flags.push(ClinicalFlag.EMERGENCY);
  if (patient.isHighCost) flags.push(ClinicalFlag.HIGH_COST);
  if (patient.isHighRisk) flags.push(ClinicalFlag.HIGH_RISK);
  return flags;
};

const formatDob = (dob) => {
  if (!dob) return '—';
  const d = new Date(dob);
  if (isNaN(d.getTime())) return String(dob);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

export default function PatientInfoCard({ patient, advancedMdMatch, onBack }) {
  if (!patient) return null;

  const flags = getClinicalFlags(patient);
  const isInactive = patient.status === MemberStatus.INACTIVE;

  // Determine AdvancedMD match status (estructura: [{candidate: {...}, score}])
  let matchStatus = null;
  if (advancedMdMatch) {
    if (advancedMdMatch.error) {
      matchStatus = { found: false, label: 'Match Error' };
    } else {
      let bestMatch = null;
      if (advancedMdMatch.candidate) {
        // Respuesta directa: { found, score, candidate: {...} }
        bestMatch = advancedMdMatch;
      } else if (Array.isArray(advancedMdMatch) && advancedMdMatch.length > 0) {
        bestMatch = advancedMdMatch[0];
      } else if (advancedMdMatch.matches && advancedMdMatch.matches.length > 0) {
        bestMatch = advancedMdMatch.matches[0];
      } else if (advancedMdMatch.bestMatch) {
        bestMatch = advancedMdMatch;
      }

      if (bestMatch) {
        const candidate = bestMatch.candidate ?? bestMatch;
        // Score: puede ser 0-1 (porcentaje) o número entero
        const rawScore = bestMatch.score ?? bestMatch.matchScore ?? candidate?.score ?? null;
        let scoreDisplay = null;
        if (rawScore != null && rawScore !== '') {
          const n = Number(rawScore);
          if (!Number.isNaN(n)) {
            scoreDisplay = n <= 1 ? Math.round(n * 100) : Math.round(n);
          }
        }
        matchStatus = { found: !!candidate?.id, label: candidate?.id ? 'Matched' : 'No Match', score: scoreDisplay };
      } else {
        matchStatus = { found: false, label: 'No Match' };
      }
    }
  }

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Stack spacing={1.5}>
        {/* Back button */}
        <Box>
          <Button
            size="small"
            startIcon={<ArrowBackIcon />}
            onClick={onBack}
          >
            Back to Patient List
          </Button>
        </Box>

        {/* Patient info row */}
        <Stack direction="row" spacing={3} flexWrap="wrap" alignItems="center">
          <Typography variant="h6" fontWeight={600}>
            {patient.memberFullName || patient.name}
          </Typography>

          <Chip
            size="small"
            icon={isInactive ? <PersonOffOutlinedIcon /> : undefined}
            label={isInactive ? 'Inactive' : 'Active'}
            color={isInactive ? 'error' : 'success'}
            variant="outlined"
            sx={{ borderRadius: 2 }}
          />
        </Stack>

        {/* Details */}
        <Stack direction="row" spacing={4} flexWrap="wrap">
          <Typography variant="body2" color="text.secondary">
            <strong>Member ID:</strong> {patient.memberId || patient.document || '—'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>DOB:</strong> {formatDob(patient.dateOfBirth)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>Gender:</strong> {patient.gender || '—'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>PCP:</strong> {patient.pcpName || '—'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>Medical Center:</strong> {patient.medicalCenter || '—'}
          </Typography>
        </Stack>

        {/* Clinical flags */}
        {flags.length > 0 && (
          <Stack direction="row" spacing={0.5} flexWrap="wrap">
            {flags.map((flag) => (
              <Chip
                key={flag}
                size="small"
                label={flag}
                color={FLAG_COLORS[flag] || 'default'}
                sx={{ borderRadius: 2 }}
              />
            ))}
          </Stack>
        )}

        {/* AdvancedMD match status */}
        {matchStatus && (
          <Stack direction="row" spacing={1} alignItems="center">
            {matchStatus.found ? (
              <CheckCircleOutlineIcon color="success" fontSize="small" />
            ) : (
              <ErrorOutlineIcon color="warning" fontSize="small" />
            )}
            <Typography variant="body2" color={matchStatus.found ? 'success.main' : 'warning.main'}>
              AdvancedMD: {matchStatus.label}
              {matchStatus.score != null && ` · ${matchStatus.score}%`}
            </Typography>
          </Stack>
        )}
      </Stack>
    </Paper>
  );
}
